import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import {
  Close as CloseIcon, Edit as EditIcon, Print as PrintIcon, Save as SaveIcon, Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useClinicPageBreakpoints } from '../../hooks/useClinicPageBreakpoints';
import {
  MOBILE_FORM_SURFACE_CLASS,
  mobileDialogFormContentSx,
  mobileFormFieldLabelProps,
  mobileFullScreenDialogPaperSx,
} from '../../utils/mobileFormLayout';
import {
  emptyResponsesForType,
  prefillInitialFromPatient,
  prefillReEvaluationFromPrior,
} from '../../constants/physiotherapyAssessmentSchema';
import { normalizePostureAssessment } from '../../constants/posturalAssessmentSchema';
import type {
  PhysioInitialResponses,
  PhysioReEvaluationResponses,
  PhysiotherapyAssessmentRecord,
  PhysiotherapyAssessmentType,
} from '../../types/physiotherapyAssessment';
import {
  createPhysiotherapyAssessment,
  getPhysiotherapyAssessment,
  getPhysiotherapyAssessmentPrintData,
  updatePhysiotherapyAssessment,
} from '../../services/physiotherapyAssessmentService';
import { buildPhysiotherapyAssessmentPrintHtml } from '../../utils/physiotherapyAssessmentPrintDocument';
import { printClinicHtml } from '../../utils/clinicPrintHtml';
import PhysiotherapyAssessmentForm from './PhysiotherapyAssessmentForm';
import {
  cloudApiDialogCancelButtonSx,
  cloudApiDialogContentSx,
  cloudApiDialogFooterSx,
  cloudApiDialogHeaderIconSx,
  cloudApiDialogHeaderSx,
  cloudApiDialogPaperSx,
  cloudApiDialogPrimaryButtonSx,
  cloudApiOutlinedButtonSx,
} from '../../pages/whatsappCloudApiTheme';

export type PhysiotherapyAssessmentDialogMode = 'create' | 'view' | 'edit';

export interface PhysiotherapyAssessmentEditorDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  patient?: any;
  existingId?: string | null;
  mode?: PhysiotherapyAssessmentDialogMode;
  defaultType?: PhysiotherapyAssessmentType;
  priorAssessmentId?: string | null;
  linkedProfessionalId?: string | null;
  linkedAppointmentId?: string | null;
  onSaved?: () => void;
  onRequestEdit?: () => void;
}

const PhysiotherapyAssessmentEditorDialog: React.FC<PhysiotherapyAssessmentEditorDialogProps> = ({
  open,
  onClose,
  patientId,
  patient,
  existingId = null,
  mode = 'create',
  defaultType = 'initial',
  priorAssessmentId = null,
  linkedProfessionalId = null,
  linkedAppointmentId = null,
  onSaved,
  onRequestEdit,
}) => {
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit' || mode === 'create';
  const { user } = useAuth();
  const { isTabletOrDown } = useClinicPageBreakpoints();
  const compactDialog = isTabletOrDown;
  const unitId = user?.current_unit?.id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recordId, setRecordId] = useState<string | null>(existingId);
  const [assessmentType, setAssessmentType] = useState<PhysiotherapyAssessmentType>(defaultType);
  const [assessmentDate, setAssessmentDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [status, setStatus] = useState<'draft' | 'finalized'>('draft');
  const [professionalId, setProfessionalId] = useState(linkedProfessionalId || user?.professional_id || '');
  const [responses, setResponses] = useState<PhysioInitialResponses | PhysioReEvaluationResponses>(
    emptyResponsesForType(defaultType),
  );

  const resetForNew = useCallback(() => {
    const type = defaultType;
    let base = emptyResponsesForType(type);
    if (type === 'initial' && patient) {
      base = prefillInitialFromPatient(base as PhysioInitialResponses, patient);
    }
    setRecordId(null);
    setAssessmentType(type);
    setAssessmentDate(new Date().toISOString().split('T')[0]);
    setStatus('draft');
    setProfessionalId(linkedProfessionalId || user?.professional_id || '');
    setResponses(base);
    setError(null);
  }, [defaultType, patient, linkedProfessionalId, user?.professional_id]);

  const loadExisting = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const data: PhysiotherapyAssessmentRecord = await getPhysiotherapyAssessment(id);
      setRecordId(data.id);
      setAssessmentType(data.assessment_type);
      setAssessmentDate(data.assessment_date);
      setStatus(data.status);
      setProfessionalId(data.professional_id || '');
      const base = data.responses || emptyResponsesForType(data.assessment_type);
      if (data.assessment_type === 'initial' && base && typeof base === 'object') {
        (base as PhysioInitialResponses).posture = normalizePostureAssessment((base as PhysioInitialResponses).posture);
      }
      setResponses(base);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao carregar avaliação');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (existingId) {
      loadExisting(existingId);
      return;
    }
    resetForNew();
    if (defaultType === 're_evaluation' && priorAssessmentId) {
      getPhysiotherapyAssessment(priorAssessmentId)
        .then((prior) => {
          if (prior.assessment_type !== 'initial' || !prior.responses) return;
          setResponses(prefillReEvaluationFromPrior(
            emptyResponsesForType('re_evaluation') as PhysioReEvaluationResponses,
            prior.responses as PhysioInitialResponses,
          ));
        })
        .catch(() => {});
    }
  }, [open, existingId, loadExisting, resetForNew, defaultType, priorAssessmentId]);

  const handleTypeChange = (type: PhysiotherapyAssessmentType) => {
    setAssessmentType(type);
    setResponses(emptyResponsesForType(type));
    if (type === 'initial' && patient) {
      setResponses(prefillInitialFromPatient(emptyResponsesForType('initial') as PhysioInitialResponses, patient));
    }
  };

  const handleSave = async (finalize = false) => {
    if (!unitId) {
      setError('Selecione uma unidade para salvar a avaliação.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        patient_id: patientId,
        unit_id: unitId,
        professional_id: professionalId || null,
        appointment_id: linkedAppointmentId || null,
        prior_assessment_id: priorAssessmentId || null,
        assessment_type: assessmentType,
        assessment_date: assessmentDate,
        status: finalize ? 'finalized' : status,
        responses,
      };
      let saved: PhysiotherapyAssessmentRecord;
      if (recordId) {
        saved = await updatePhysiotherapyAssessment(recordId, payload);
      } else {
        saved = await createPhysiotherapyAssessment(payload);
        setRecordId(saved.id);
      }
      setStatus(saved.status);
      onSaved?.();
      if (finalize) onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao salvar avaliação');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = async () => {
    if (!recordId) {
      setError('Salve a avaliação antes de imprimir.');
      return;
    }
    try {
      const printData = await getPhysiotherapyAssessmentPrintData(recordId);
      const html = buildPhysiotherapyAssessmentPrintHtml(printData);
      printClinicHtml(html);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao imprimir');
    }
  };

  const typeLabel = assessmentType === 're_evaluation' ? 'Reavaliação' : 'Avaliação inicial';
  const dialogTitle = isViewMode
    ? `Visualizar ${typeLabel}`
    : recordId
      ? `Editar ${typeLabel}`
      : `Nova ${typeLabel}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={compactDialog}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          ...(compactDialog ? mobileFullScreenDialogPaperSx : cloudApiDialogPaperSx),
          minHeight: compactDialog ? undefined : '85vh',
          ...(compactDialog ? {
            '& .MuiDialogTitle-root': { flexShrink: 0, position: 'relative', zIndex: 2 },
            '& .MuiDialogActions-root': {
              flexShrink: 0,
              position: 'sticky',
              bottom: 0,
              zIndex: 2,
              bgcolor: '#fff',
              borderTop: '1px solid #e2e8f0',
              pb: 'max(12px, env(safe-area-inset-bottom, 0px))',
            },
          } : {}),
        },
      }}
    >
      <DialogTitle sx={{ ...cloudApiDialogHeaderSx, position: 'relative', pr: compactDialog ? 2 : 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%', pr: compactDialog ? 5 : 4 }}>
          <Box sx={cloudApiDialogHeaderIconSx}><AssignmentIcon /></Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, fontSize: compactDialog ? '1rem' : undefined }}>
              {dialogTitle}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
              {patient?.full_name || 'Paciente'} · Formulário Estúdio Forma
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: compactDialog ? 8 : 12,
              bgcolor: compactDialog ? '#f1f5f9' : 'transparent',
              border: compactDialog ? '1px solid #cbd5e1' : 'none',
              '&:hover': { bgcolor: compactDialog ? '#e2e8f0' : undefined },
            }}
            aria-label="Fechar"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        {compactDialog && (
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            startIcon={<CloseIcon />}
            sx={{ mt: 1.25, textTransform: 'none', fontWeight: 700, borderColor: '#94a3b8', color: '#334155' }}
          >
            Fechar avaliação
          </Button>
        )}
      </DialogTitle>

      <DialogContent
        className={compactDialog ? MOBILE_FORM_SURFACE_CLASS : undefined}
        sx={{ ...cloudApiDialogContentSx, ...mobileDialogFormContentSx(compactDialog), bgcolor: '#f8fafc' }}
      >
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', overflow: 'visible', pt: compactDialog ? 0.5 : 0 }}>
              <FormControl size="small" sx={{ minWidth: 180, width: compactDialog ? '100%' : undefined, overflow: 'visible' }} disabled={!!recordId || isViewMode}>
                <InputLabel shrink={compactDialog || undefined}>Tipo</InputLabel>
                <Select
                  label="Tipo"
                  value={assessmentType}
                  onChange={(e) => handleTypeChange(e.target.value as PhysiotherapyAssessmentType)}
                >
                  <MenuItem value="initial">Avaliação inicial</MenuItem>
                  <MenuItem value="re_evaluation">Reavaliação</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="date"
                label="Data"
                InputLabelProps={mobileFormFieldLabelProps(compactDialog) ?? { shrink: true }}
                value={assessmentDate}
                onChange={(e) => setAssessmentDate(e.target.value)}
                disabled={isViewMode}
                sx={{
                  width: compactDialog ? '100%' : undefined,
                  overflow: 'visible',
                  '& .MuiInputBase-input': {
                    fontSize: compactDialog ? '16px' : '0.875rem',
                    lineHeight: 1.4,
                    minHeight: compactDialog ? 40 : 'auto',
                  },
                }}
              />
              <Chip
                size="small"
                label={status === 'finalized' ? 'Finalizada' : 'Rascunho'}
                sx={{
                  fontWeight: 700,
                  bgcolor: status === 'finalized' ? '#dcfce7' : '#fef3c7',
                  color: status === 'finalized' ? '#16a34a' : '#d97706',
                }}
              />
            </Box>
            <PhysiotherapyAssessmentForm
              assessmentType={assessmentType}
              responses={responses}
              onChange={setResponses}
              patientId={patientId}
              readOnly={isViewMode}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          ...cloudApiDialogFooterSx,
          ...(compactDialog ? {
            flexDirection: 'column',
            alignItems: 'stretch',
            px: 2,
            py: 1.5,
            gap: 1,
            '& .MuiButton-root': { width: '100%', justifyContent: 'center', m: 0 },
          } : { flexWrap: 'wrap' }),
        }}
      >
        <Button onClick={onClose} sx={cloudApiDialogCancelButtonSx} disabled={saving}>
          {isViewMode ? 'Fechar' : 'Cancelar'}
        </Button>
        {recordId && (
          <Button startIcon={<PrintIcon />} onClick={handlePrint} sx={cloudApiOutlinedButtonSx} disabled={saving}>
            Imprimir
          </Button>
        )}
        {isViewMode && onRequestEdit && (
          <Button startIcon={<EditIcon />} onClick={onRequestEdit} variant="contained" sx={cloudApiDialogPrimaryButtonSx}>
            Editar
          </Button>
        )}
        {isEditMode && (
          <>
            <Button startIcon={<SaveIcon />} onClick={() => handleSave(false)} sx={cloudApiOutlinedButtonSx} disabled={saving || loading}>
              {saving ? 'Salvando…' : 'Salvar rascunho'}
            </Button>
            <Button variant="contained" onClick={() => handleSave(true)} sx={cloudApiDialogPrimaryButtonSx} disabled={saving || loading}>
              {saving ? 'Salvando…' : 'Finalizar'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PhysiotherapyAssessmentEditorDialog;
